import { ImageOff } from "lucide-react";
import { BulkStatusBadge } from "@/components/links/BulkResultTable";
import { conversionMessage, linkTypeLabel, originLabel } from "@/lib/affiliate-ui";
import type { LinkHistoryRow } from "@/lib/hooks/useLinkHistory";
import { formatPriceCents, marketplaceLabel } from "@/lib/marketplace";
import { safeHref, shortLinkUrl } from "@/lib/short-links";

const HEADERS = ["Imagem", "Título", "Tipo", "Link", "Preço", "Cupom", "Status", "Destino", "Data"] as const;

/** Link exibido: o curto (Worker) quando existe, senao o destino oficial. */
export function historyLink(row: LinkHistoryRow): string | null {
  if (row.short_code) {
    return shortLinkUrl(row.short_code);
  }
  return safeHref(row.destination_url);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/** Tabela do historico unificado (rola na horizontal no celular). */
export function HistoryTable({ rows }: { rows: readonly LinkHistoryRow[] }): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[960px] text-left text-sm">
        <thead className="bg-[hsl(var(--surface-raised)/0.45)] text-xs uppercase tracking-[0.06em] text-muted-foreground">
          <tr>
            {HEADERS.map((header) => (
              <th key={header} scope="col" className="px-3 py-2 font-semibold">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => (
            <HistoryRow key={row.id} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryRow({ row }: { row: LinkHistoryRow }): JSX.Element {
  const link = historyLink(row);
  const status = row.status === "pending" ? "ignored" : row.status;
  return (
    <tr className="align-top">
      <td className="px-3 py-2">
        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded border border-border bg-background/60">
          {row.image_url ? (
            <img src={row.image_url} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <ImageOff size={14} className="text-muted-foreground" aria-hidden="true" />
          )}
        </div>
      </td>
      <td className="max-w-[220px] px-3 py-2">
        <p className="line-clamp-2 text-foreground">{row.title ?? row.original_url ?? "-"}</p>
        <p className="text-[11px] text-muted-foreground">
          {row.marketplace_code ? marketplaceLabel(row.marketplace_code) : "-"} · {originLabel(row.origin)}
        </p>
      </td>
      <td className="px-3 py-2 text-muted-foreground">{linkTypeLabel(row.link_type)}</td>
      <td className="max-w-[240px] px-3 py-2">
        {link ? (
          <a href={link} target="_blank" rel="noreferrer noopener" className="break-all font-mono text-xs text-primary hover:underline">
            {link}
          </a>
        ) : (
          <span className="text-xs text-muted-foreground">{conversionMessage(row.error_code)}</span>
        )}
      </td>
      <td className="px-3 py-2 text-foreground">{formatPriceCents(row.price_cents) ?? "-"}</td>
      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{row.coupon ?? "-"}</td>
      <td className="px-3 py-2"><BulkStatusBadge status={status} /></td>
      <td className="max-w-[160px] px-3 py-2 text-muted-foreground">{row.destination ?? "-"}</td>
      <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{formatDate(row.created_at)}</td>
    </tr>
  );
}
